import { CompRef } from '@wix/document-services-types';
import type {
  EditorElement,
  InlineElement,
  ElementItem,
  Elements,
} from '@wix/component-protocol/schema';
import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

function getInnerElementsChildren(element: ElementItem): Elements | undefined {
  return (
    element.inlineElement?.elements ||
    element.refElement?.resolvedElement?.elements
  );
}

export class ManifestStructureService {
  constructor(private editorPointerAPI: EditorPointerServiceAPI) {}

  getAncestors(editorPointer: EditorPointer<CompRef>) {
    const rootDocumentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);

    if (!editorPointer.innerElementPath?.length) {
      return [];
    }

    const allPointers = editorPointer.innerElementPath.reduce(
      (acc: EditorPointer<CompRef>[], childKey: string) => {
        const currentEditorPointer = acc[acc.length - 1];

        const documentPointer =
          this.editorPointerAPI.getDocumentPointer(currentEditorPointer);
        const innerElementPath =
          this.editorPointerAPI.getInnerElementPath(currentEditorPointer) ?? [];

        return [
          ...acc,
          this.editorPointerAPI.buildEditorPointer(documentPointer, [
            ...innerElementPath,
            childKey,
          ]),
        ];
      },
      [this.editorPointerAPI.buildEditorPointer(rootDocumentPointer)],
    );

    // Exclude the current element pointer
    return allPointers.slice(0, -1);
  }

  getChildren(
    editorPointer: EditorPointer<CompRef>,
    componentManifest: EditorElement | InlineElement | null | undefined,
    options: {
      isRecursive?: boolean;
    } = {
      isRecursive: true,
    },
  ): EditorPointer<CompRef>[] {
    if (!componentManifest || !componentManifest.elements) {
      return [];
    }

    const innerElements = componentManifest.elements;
    return Object.entries(innerElements || {}).flatMap(([key, element]) => {
      if (element === undefined) {
        return [];
      }

      const childPointer = this.editorPointerAPI.buildChildEditorPointer(
        editorPointer,
        [key],
      );

      const innerElementsChildren = getInnerElementsChildren(element);

      if (innerElementsChildren && options.isRecursive) {
        return [
          childPointer,
          ...this.getChildren(
            childPointer,
            element.inlineElement || element.refElement?.resolvedElement,
            {
              isRecursive: true,
            },
          ),
        ];
      } else {
        return [childPointer];
      }
    });
  }

  getChildrenElementManifests(
    manifest: EditorElement | InlineElement | null | undefined,
  ) {
    if (!manifest || !manifest.elements) {
      return {};
    }

    const manifests: Record<string, EditorElement | InlineElement> = {};

    Object.entries(manifest.elements || {}).forEach(([path, element]) => {
      const _manifest =
        element?.inlineElement || element?.refElement?.resolvedElement;

      if (_manifest) {
        manifests[path] = _manifest;

        const children = this.getChildrenElementManifests(_manifest);

        Object.keys(children).forEach((childPath) => {
          manifests[`${path}.${childPath}`] = children[childPath];
        });
      }
    });

    return manifests;
  }
}
